import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import { Edit, Check, X } from "lucide-react";
import axios from "axios";
import { getToken, base_url } from "../../config/config";
import { toast } from "react-toastify";

const API_BASE_URL = `${base_url}/v1`;

const KundenSelectionDialog = ({
  open,
  onClose,
  onSelectKunde,
  selectedKunde,
}) => {
  const [kundenList, setKundenList] = useState([]);
  const [lieferantList, setLieferantList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editName, setEditName] = useState("");

  // Fetch kunden and lieferant list when dialog opens
  useEffect(() => {
    if (open) {
      fetchKundenAndLieferant();
    }
  }, [open]);

  // Set initial selected kunde if passed in
  useEffect(() => {
    if (selectedKunde) {
      // Add type if not present (for existing selections)
      const itemWithType = selectedKunde.type
        ? selectedKunde
        : {
            ...selectedKunde,
            type: "Kunde", // Default to Kunde if type is not specified
          };
      setSelected(itemWithType);
      setSearchTerm(selectedKunde.bezeichnung || "");
    }
  }, [selectedKunde]);

  const fetchKundenAndLieferant = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both Kunden and Lieferant
      const [kundenResponse, lieferantResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/getKunden`),
        axios.get(`${API_BASE_URL}/getLieferant`),
      ]);

      if (kundenResponse.data.data) {
        setKundenList(kundenResponse.data.data);
      }
      if (lieferantResponse.data.data) {
        setLieferantList(lieferantResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(
        "Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  // Combine and filter kunden and lieferant based on search term
  const filteredKunden = useMemo(() => {
    const combinedList = [
      ...kundenList.map((item) => ({ ...item, type: "Kunde" })),
      ...lieferantList.map((item) => ({ ...item, type: "Lieferant" })),
    ];

    return combinedList.filter(
      (item) =>
        searchTerm &&
        ((item.bezeichnung || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
          (item.konto || "").toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [kundenList, lieferantList, searchTerm]);

  const handleSelectKunde = (kunde) => {
    setSelected(kunde);
    setSearchTerm(kunde.bezeichnung);
  };

  const handleClearSelection = () => {
    setSelected(null);
    setSearchTerm("");
  };

  const handleConfirmSelection = () => {
    if (selected) {
      onSelectKunde(selected);
      onClose();
    }
  };

  const handleEditName = (item) => {
    setIsEditing(true);
    setEditingItem(item);
    setEditName(item.bezeichnung);
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !editName.trim()) {
      toast.error("Bitte geben Sie einen gültigen Namen ein");
      return;
    }

    try {
      const endpoint =
        editingItem.type === "Kunde" ? "updateKunden" : "updateLieferant";
      const response = await axios.put(
        `${API_BASE_URL}/${endpoint}/${editingItem._id}`,
        {
          bezeichnung: editName.trim(),
        }
      );

      if (response.data.success) {
        // Update the local lists
        if (editingItem.type === "Kunde") {
          setKundenList((prev) =>
            prev.map((item) =>
              item._id === editingItem._id
                ? { ...item, bezeichnung: editName.trim() }
                : item
            )
          );
        } else {
          setLieferantList((prev) =>
            prev.map((item) =>
              item._id === editingItem._id
                ? { ...item, bezeichnung: editName.trim() }
                : item
            )
          );
        }

        // Update selected if it's the same item
        if (selected && selected._id === editingItem._id) {
          setSelected({ ...selected, bezeichnung: editName.trim() });
        }

        toast.success(`${editingItem.type} erfolgreich aktualisiert`);
        setIsEditing(false);
        setEditingItem(null);
        setEditName("");
      }
    } catch (error) {
      console.error("Error updating name:", error);
      toast.error(`Fehler beim Aktualisieren des ${editingItem.type}s`);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingItem(null);
    setEditName("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Kunde/Lieferant auswählen</Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!selected ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                label="Suche nach Kunden-/Lieferantenname oder Kontonummer"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Box>

            {error && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            {loading ? (
              <Typography>Daten werden geladen...</Typography>
            ) : (
              <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
                {searchTerm && filteredKunden.length === 0 ? (
                  <Typography>Keine passenden Einträge gefunden.</Typography>
                ) : (
                  filteredKunden.map((item) => (
                    <Box
                      key={item._id}
                      sx={{
                        p: 2,
                        mb: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                      onClick={() => handleSelectKunde(item)}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          {isEditing && editingItem?._id === item._id ? (
                            <TextField
                              size="small"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleSaveEdit();
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <Typography variant="subtitle1">
                              {item.bezeichnung}
                            </Typography>
                          )}
                          <Chip
                            label={item.type}
                            size="small"
                            color={
                              item.type === "Kunde" ? "primary" : "secondary"
                            }
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Konto: {item.konto}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, ml: 1 }}>
                        {isEditing && editingItem?._id === item._id ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveEdit();
                              }}
                              sx={{ color: "success.main" }}
                            >
                              <Check size={16} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEdit();
                              }}
                              sx={{ color: "error.main" }}
                            >
                              <X size={16} />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditName(item);
                            }}
                            sx={{ color: "primary.main" }}
                          >
                            <Edit size={16} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            )}
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="h6">
                  Ausgewählter {selected.type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bitte bestätigen Sie Ihre Auswahl
                </Typography>
              </Box>
              <Button size="small" onClick={handleClearSelection}>
                Zurücksetzen
              </Button>
            </Box>

            <Box
              sx={{
                p: 3,
                bgcolor: "background.neutral",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography variant="h6">{selected.bezeichnung}</Typography>
                <Chip
                  label={selected.type}
                  size="small"
                  color={selected.type === "Kunde" ? "primary" : "secondary"}
                />
              </Box>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Konto: {selected.konto}
              </Typography>
              {selected.kurzbezeichnung && (
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Kurzbezeichnung: {selected.kurzbezeichnung}
                </Typography>
              )}
              {selected.ustIdNummer && (
                <Typography variant="body1" sx={{ mt: 1 }}>
                  USt-IdNr.: {selected.ustIdNummer}
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        {selected && (
          <Button
            onClick={handleConfirmSelection}
            variant="contained"
            color="primary"
          >
            Auswahl bestätigen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default KundenSelectionDialog;
